import { apiClient } from './client';
import { User, ApiResponse } from '@/types';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  merchantId?: number;
}

export interface RegisterSessionResponse {
  user: User;
  customer: { id: number; company_name: string; is_active: boolean; created_at: string };
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>(
      '/auth/login',
      data
    );
    return response.data.data;
  },

  register: async (data: RegisterRequest): Promise<User> => {
    const response = await apiClient.post<ApiResponse<User>>(
      '/auth/register',
      data
    );
    return response.data.data;
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  getCurrentUser: async (): Promise<User | null> => {
    const response = await apiClient.get<ApiResponse<User>>('/auth/me');
    return response.data.data ?? null;
  },

  refreshToken: async (): Promise<string> => {
    const response = await apiClient.post<ApiResponse<{ token: string }>>(
      '/auth/refresh'
    );
    return response.data.data.token;
  },

  sendMagicLink: async (email: string): Promise<void> => {
    await apiClient.post('/auth/login', { email });
  },

  registerWithMagicLink: async (data: {
    email: string;
    password: string;
    company_name: string;
    first_name: string;
    last_name: string;
    language?: string;
    country: string;
    phone: string;
    street: string;
    street_number: string;
    postal_code: string;
    city: string;
    website: string;
    billing_email?: string;
    tax_id: string;
    commercial_register?: string;
  }): Promise<RegisterSessionResponse | null> => {
    const response = await apiClient.post<RegisterSessionResponse>('/auth/register', data);
    // If auto-confirm is enabled, backend returns session data
    if (response.data?.access_token) {
      return response.data;
    }
    return null;
  },

  checkEmailExists: async (email: string): Promise<boolean> => {
    const response = await apiClient.get<{ exists: boolean }>('/auth/check-email', {
      params: { email },
    });
    return response.data.exists;
  },

  verifyToken: async (token: string): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>(
      '/auth/verify',
      { token }
    );
    return response.data.data;
  },
};
